import { noop, keys, isEmpty } from 'lodash/fp';
import { getJwtToken } from 'app/store/auth';
import {
    HTTP_NOT_OK,
    HTTP_OK,
    HTTP_FORBIDDEN,
    HTTP_UNAUTHORIZED,
    HTTP_SERVER_TIMEOUT,
    HTTP_INTERNAL_SERVER_ERROR,
    HTTP_CLIENT_TIMEOUT
} from './constants';

export const extractData = ({ data }) => data;

const ENDPOINT_METHOD_MAP = {
    query: { method: 'PUT', url: '/api/op' },
    command: { method: 'PUT', url: '/api/op' },
    fileupload: { method: 'POST', url: '/api/dataUpload' },
    op: { method: 'PUT', url: '/api/op' },
    snowflake: { method: 'PUT', url: '/api/snowflake' },
    dapMigrationUpload: { method: 'POST', url: '/api/dapMigrationUpload' }
};

export const apiClient = ({ endpoint, _type, meta = {}, context, ...params }) => {
    const handlers = {
        onProgress: noop,
        onSuccess: noop,
        onError: noop,
        onForbidden: noop,
        onAbort: noop,
        onUnAuth: noop,
        onTimeout: noop,
        onFinish: noop,
        onForeignError: noop
    };

    const uploadProgressHandler = (file) => {
        const percentComplete = file.loaded / file.total;
        handlers.onProgress(percentComplete);
    };

    const client = () => {
        const xhr = new XMLHttpRequest();

        xhr.onreadystatechange = () => {
            if (xhr.readyState !== 4) {
                return;
            }

            let responseData;
            let error = {
                code: xhr.status,
                messages: [xhr.statusText]
            };

            try {
                const { data, errors } = JSON.parse(xhr.responseText);
                responseData = data;
                if (isEmpty(data) && errors?.length) {
                    // we only consume errors of the response if it has no payload.
                    // In case there are multiple response errors, we just take the first one since downstream code is not prepared for more.
                    error = { ...error, data: errors[0] };
                }
            } catch {
                // don't fail on parsing
            }

            const originServer = xhr.getResponseHeader('origin-server');

            if (originServer !== 'Datameer' && xhr.status >= HTTP_NOT_OK) {
                console.error('Foreign Error: ', xhr.status, xhr.response);

                // foreign errors might come back as 403 or some other error code
                // that our stack handles specially
                // so here we recode it as a gheenric 500 error with a generic message
                // as not all foreign services (eg cloudfalre) include a status message
                handlers.onForeignError({
                    code: 500,
                    messages: ['Infrastructure Error', xhr.responseText]
                });
            } else if (xhr.status === 0) {
                handlers.onAbort(meta);
            } else if (xhr.status >= HTTP_OK && xhr.status < HTTP_NOT_OK) {
                handlers.onSuccess(responseData, meta);
            } else if (xhr.status === HTTP_UNAUTHORIZED) {
                handlers.onUnAuth(error, meta);
            } else if (xhr.status === HTTP_FORBIDDEN) {
                handlers.onForbidden(error, meta);
            } else if (xhr.status === HTTP_SERVER_TIMEOUT) {
                handlers.onTimeout(error, meta, responseData);
            } else {
                handlers.onError(
                    isEmpty(responseData) ? error : { ...error, data: responseData },
                    meta
                );
            }

            handlers.onFinish(meta);
        };

        xhr.open(ENDPOINT_METHOD_MAP[endpoint].method, ENDPOINT_METHOD_MAP[endpoint].url);

        const jwt = getJwtToken();

        if (jwt) {
            xhr.setRequestHeader('Authorization', `Bearer ${jwt}`);
        }

        if (endpoint === 'fileupload' || endpoint === 'dapMigrationUpload') {
            xhr.upload.addEventListener('progress', uploadProgressHandler, false);
            xhr.send(params.formData);
        } else {
            xhr.setRequestHeader('Content-Type', 'application/json');

            const _context = { _request_id_: meta.requestId, client: context, meta };
            xhr.send(JSON.stringify({ _type, _context, ...params }));
        }

        xhr.onerror = () => {
            if (endpoint === 'fileupload' || endpoint === 'dapMigrationUpload') {
                handlers.onError(
                    {
                        messages: [
                            'Oops! The upload was aborted due to an error. This error can occur if you try to upload a file bigger than 100 megabytes.'
                        ],
                        code: HTTP_INTERNAL_SERVER_ERROR
                    },
                    meta
                );
            } else {
                console.error('API client XHR error', ENDPOINT_METHOD_MAP[endpoint].url, {
                    meta,
                    context,
                    params
                });
                handlers.onError(
                    { messages: ['Connection error.'], code: HTTP_INTERNAL_SERVER_ERROR },
                    meta
                );
            }
        };

        xhr.onabort = (event) => {
            console.warn(event);
            handlers.onAbort(meta);
        };

        xhr.ontimeout = (event) => {
            console.warn(event);
            handlers.onTimeout(
                { messages: ['Request timed out'], code: HTTP_CLIENT_TIMEOUT },
                meta
            );
        };

        return () => xhr.abort();
    };

    keys(handlers).forEach((key) => {
        client[key] = (handler) => {
            handlers[key] = handler;

            return client;
        };
    });

    return client;
};
